import type React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useExpenses, useCreateExpense, useUpdateExpense, EXPENSE_CATEGORIES } from '../../hooks/useExpenses'
import { usePrinters } from '../../hooks/usePrinters'
import { BackLink } from '../../components/BackLink'

export default function ExpenseForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)

  const { data: existingExpenses } = useExpenses()
  const createExpense = useCreateExpense()
  const updateExpense = useUpdateExpense()
  const { data: printers } = usePrinters()

  const existing = existingExpenses?.find((e) => e.id === id)

  const today = new Date().toISOString().slice(0, 10)
  const [expenseDate, setExpenseDate] = useState(existing?.expense_date ?? today)
  const [amount, setAmount] = useState(existing ? String(existing.amount) : '')
  const [category, setCategory] = useState<string>(existing?.category ?? 'printer-part')
  const [printerId, setPrinterId] = useState(existing?.printer_id ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (isEdit && id) {
      try {
        await updateExpense.mutateAsync({
          id,
          expense_date: expenseDate,
          amount: Number(amount),
          category,
          printer_id: printerId || null,
          description: description || null,
          notes: notes || null,
        })
        navigate('/money/expenses')
      } catch (err) {
        setErrorMessage((err as Error).message)
      }
      return
    }

    try {
      await createExpense.mutateAsync({
        expense_date: expenseDate,
        amount: Number(amount),
        category,
        printer_id: printerId || null,
        description: description || null,
        notes: notes || null,
      })
      navigate('/money/expenses')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const isPending = isEdit ? updateExpense.isPending : createExpense.isPending

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/money/expenses" />
      <h1>{isEdit ? 'Edit expense' : 'Log an expense'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Date" value={expenseDate} onChange={setExpenseDate} type="date" required />
        <Field label="Amount ($)" value={amount} onChange={setAmount} type="decimal" required />
        <Field label="Category" value={category} onChange={setCategory} select={EXPENSE_CATEGORIES as unknown as string[]} />
        <Field
          label="Printer"
          value={printerId}
          onChange={setPrinterId}
          selectOptions={[
            { value: '', label: 'Shared / not specific' },
            ...(printers ?? []).map((p) => ({ value: p.id, label: p.name })),
          ]}
          hint="Optional — tag a specific printer if this expense is for it"
        />
        <Field label="Description (optional)" value={description} onChange={setDescription} />
        <Field label="Notes (optional)" value={notes} onChange={setNotes} multiline />

        <button
          type="submit"
          disabled={isPending}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save expense'}
        </button>

        {errorMessage && (
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', hint, required, multiline, select, selectOptions }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  multiline?: boolean
  select?: string[]
  selectOptions?: { value: string; label: string }[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 320,
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  const textareaStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {selectOptions ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {selectOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      ) : select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} style={textareaStyle} />
      ) : (
        <input
          type={type === 'date' ? 'date' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
        />
      )}
      {hint && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>{hint}</p>}
    </div>
  )
}
